import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import PageSeo from "../components/PageSeo";
import { supabase } from "../supabaseClient";
import "./About.css";

const initialFormState = {
  name: "",
  email: "",
  phone: "",
  feedbackType: "feature",
  message: "",
};

export default function Feedback() {
  const [formData, setFormData] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!mounted || !user) return;
      setFormData((prev) => ({
        ...prev,
        name: prev.name || user.user_metadata?.name || "",
        email: prev.email || user.email || "",
        phone: prev.phone || user.phone || "",
      }));
    };
    loadUser();
    return () => {
      mounted = false;
    };
  }, []);

  const messageLength = formData.message.trim().length;
  const canSubmit = useMemo(
    () => Boolean(formData.name.trim()) && Boolean(formData.email.trim()) && messageLength >= 10 && messageLength <= 1000,
    [formData.email, formData.name, messageLength]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setErrorMessage("");
    setSubmitted(false);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) {
      setErrorMessage("Please fill required fields and keep message between 10 and 1000 characters.");
      return;
    }
    setIsLoading(true);
    setErrorMessage("");
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        feedback_type: formData.feedbackType,
        message: formData.message.trim(),
      };
      const { error } = await supabase.from("feedback").insert([payload]);
      if (error) {
        setErrorMessage("Error submitting feedback. Please try again.");
        return;
      }
      setSubmitted(true);
      setFormData({
        ...initialFormState,
        name: user?.user_metadata?.name || "",
        email: user?.email || "",
        phone: user?.phone || "",
      });
    } catch (error) {
      setErrorMessage("Feedback could not be submitted right now.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PageSeo
        title="Feedback & Suggestions"
        description="Share bugs, ideas and feature requests to improve MotoHub India."
        keywords="MotoHub feedback, bike app suggestions, report bug MotoHub"
        path="/feedback"
      />

      <div className="aboutx-root">
        <div className="aboutx-wrap aboutx-columns">
          <motion.article className="aboutx-panel" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="aboutx-kicker">Feedback</p>
            <h3>Help us build a better MotoHub India.</h3>
            <p>Report issues, suggest improvements, and request features. Your input directly shapes the roadmap.</p>
          </motion.article>

          <motion.article className="aboutx-panel" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
            <p className="aboutx-kicker">Guidelines</p>
            <p>Include clear steps for bugs, your bike/use case, and expected behavior. This helps us ship fixes faster.</p>
          </motion.article>
        </div>

        <div className="aboutx-wrap">
          <motion.form className="aboutx-panel" onSubmit={handleSubmit} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {submitted ? <div style={okStyle}>Feedback submitted successfully.</div> : null}
            {errorMessage ? <div style={errStyle}>{errorMessage}</div> : null}

            <div style={formGrid}>
              <input name="name" value={formData.name} onChange={handleChange} placeholder="Name" required />
              <input name="email" value={formData.email} onChange={handleChange} placeholder="Email" required />
              <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone" />
              <select name="feedbackType" value={formData.feedbackType} onChange={handleChange}>
                <option value="feature">Feature</option>
                <option value="bug">Bug</option>
                <option value="improvement">Improvement</option>
              </select>
            </div>

            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Your message"
              required
              style={{ minHeight: "170px", marginTop: "12px" }}
            />

            <button type="submit" disabled={isLoading || !canSubmit} style={submitBtn}>
              {isLoading ? "Sending..." : "Send Feedback"}
            </button>
          </motion.form>
        </div>
      </div>
    </>
  );
}

const formGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "12px",
};

const submitBtn = {
  marginTop: "14px",
  width: "100%",
  padding: "12px",
  borderRadius: "12px",
  border: "none",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
  background: "linear-gradient(120deg, #f59e0b, #ea580c)",
};

const okStyle = {
  marginBottom: "12px",
  padding: "10px 12px",
  borderRadius: "10px",
  color: "#bbf7d0",
  border: "1px solid rgba(34,197,94,0.4)",
  background: "rgba(34,197,94,0.12)",
};

const errStyle = {
  marginBottom: "12px",
  padding: "10px 12px",
  borderRadius: "10px",
  color: "#fecaca",
  border: "1px solid rgba(239,68,68,0.4)",
  background: "rgba(239,68,68,0.12)",
};
